import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  addSupportTicketComment,
  closeSupportTicket,
  createSupportTicket,
  getSupportTicketDetail,
  getSupportTickets,
  reopenSupportTicket,
  uploadSupportTicketAttachment,
} from '../services/supportTicketService';
import {
  CreateSupportTicketCommentRequest,
  CreateSupportTicketRequest,
  SupportTicketDetail,
  SupportTicketListResponse,
  SupportTicketStatusFilter,
} from '../models/supportTickets';

export const useInfiniteSupportTickets = (
  status: SupportTicketStatusFilter,
  enabled = true
) => {
  return useInfiniteQuery<SupportTicketListResponse>({
    queryKey: ['support-tickets', status],
    queryFn: ({ pageParam }) =>
      getSupportTickets({ status, cursor: pageParam as string | undefined }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled,
    staleTime: 10_000,
    refetchOnWindowFocus: false,
  });
};

export const useSupportTicketDetail = (issueId?: string) => {
  return useQuery<SupportTicketDetail>({
    queryKey: ['support-ticket-detail', issueId],
    queryFn: () => getSupportTicketDetail(issueId!),
    enabled: !!issueId,
    staleTime: 10_000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateSupportTicket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSupportTicketRequest) => createSupportTicket(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });
};

export const useAddSupportTicketComment = (issueId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSupportTicketCommentRequest) =>
      addSupportTicketComment(issueId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket-detail', issueId] });
    },
  });
};

export const useCloseSupportTicket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => closeSupportTicket(issueId),
    onSuccess: (_data, issueId) => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket-detail', issueId] });
    },
  });
};

export const useReopenSupportTicket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => reopenSupportTicket(issueId),
    onSuccess: (_data, issueId) => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket-detail', issueId] });
    },
  });
};

export const useUploadSupportTicketAttachment = () => {
  return useMutation({
    mutationFn: (file: File) => uploadSupportTicketAttachment(file),
  });
};
